<template>
  <div v-if="schoolData" class="container-fluid section-p70">
    <div class="row">
      <div class="col-auto title-blue">
        {{ t("MANAGE_SCHOOL") }}
      </div>
      <div class="col align-self-center">
        <Button
            :label="isSchoolArchived ? t('ARCHIVED') : t('ACTIVE')"
            :severity="isSchoolArchived ? 'danger' : 'primary'"
            v-tooltip="isSchoolArchived ? t('UNARCHIVE') : t('ARCHIVE')"
            @click="toggleSchoolArchive()"
        />
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-xl-3">
        <Card class="text-start manage-school-card">
          <template #title>{{ t("BASE_DATA") }}</template>
          <template #content>
            <form id="schoolForm" ref="form" @submit.prevent="updateSchool">
              <div class="mb-2">
                <label for="code" class="form-label">{{ t('SCHOOL_CODE') }}</label>
                <InputText
                    id="code"
                    maxlength="8"
                    v-model="schoolData.registryCode"
                    :disabled="!schoolData.canUpdate"
                    :invalid="schoolData.registryCode && !isValidRegistryCode"
                    class="w-100"
                />
                <small v-if="schoolData.registryCode && !isValidRegistryCode" class="text-danger">{{ t('ENTER_8_DIGIT_VALUE') }}</small>
              </div>

              <div class="mb-2">
                <label for="type" class="form-label">{{ t('SCHOOL_NAME') }}</label>
                <InputText
                    id="type"
                    v-model="schoolData.businessName"
                    :disabled="!schoolData.canUpdate"
                    :invalid="!schoolData.businessName"
                    class="w-100"
                />
                <small v-if="!schoolData.businessName" class="text-danger">{{ t('ERRORS.FIELD_IS_REQUIRED') }}</small>
              </div>
              <Button v-if="schoolData.canUpdate" type="submit" :label="t('SAVE')" class="w-100"/>
            </form>
          </template>
        </Card>
      </div>

      <div class="col-xl-9">
        <Tabs value="0">
          <TabList>
            <Tab value="0">{{ t('TRAINING_GROUPS') }}</Tab>
            <Tab value="1">{{ t('SCHOOL_MANAGER') }}</Tab>
            <Tab value="2">{{ t('SCHOOL_MODULES') }}</Tab>
            <Tab value="3">{{ t('SCHOOL_TRAINERS') }}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel value="0">
              <div class="row">
                <div class="col">
                  <PaginatedTable id="paginated-schoolGroups"
                                  :fields="groupFields"
                                  :on-page-change="getSchoolGroups"
                                  :small="true"
                                  ref="groupsTableRef"
                  >
                    <template v-slot:cell(language)="{item}">
                      <span>{{ getGroupLangOptions(t).find(v => v.value === item.language)?.text ?? item.language }}</span>
                    </template>
                    <template v-slot:cell(action)="{ item }">
                      <div class="align-items-center">
                        <router-link :to="{ name: 'ManageGroup', params: { id: `${item.id}` } }">
                          <Button :label="t('VIEW')"/>
                        </router-link>
                      </div>
                    </template>
                  </PaginatedTable>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="1">
              <div class="row">
                <div class="col text-end">
                  <Button
                      :label="t('ADD')"
                      :disabled="isSchoolArchived"
                      @click="toggleSearchForm"
                  />
                </div>
              </div>

              <div class="row" v-if="isSearch">
                <div class="col">
                  <form class="searchForm">
                    <div class="mb-2">
                      <label for="search-managers" class="form-label">{{ t('PERSON_SEARCH_TIP') }}</label>
                      <SearchInput
                          input-id="search-managers"
                          :on-input="searchManager"
                          @on-change="submitManager"
                          :confirm-on-select-text="t('ADD_SCHOOL_MANAGER')"
                          :required-symbols="3"
                      />
                    </div>
                  </form>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <DataTable
                      :value="managers"
                      striped-rows
                      paginator
                      size="small"
                      :rows="perPage"
                      :rowHover="true"
                      :alwaysShowPaginator="false"
                      :removableSort="true"
                  >
                    <template #empty>{{ t('NO_RESULTS') }}</template>
                    <Column field="fullName" :header="t('NAME')" sortable/>
                    <Column field="ssn" :header="t('SSN')" sortable>
                      <template #body="{ data }">
                        <router-link :to="{ name: 'Trainee', params: { id: data.id } }">{{ data.ssn }}</router-link>
                      </template>
                    </Column>
                    <Column>
                      <template #body="{ data }">
                        <div class="text-center">
                          <Button
                              icon="pi pi-times"
                              :disabled="isSchoolArchived"
                              severity="danger"
                              v-tooltip="t('REMOVE')"
                              text
                              rounded
                              @click="removeManager(data.id)"
                          />
                        </div>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="2">
              <div class="row" v-if="missingModules?.length">
                <div class="col text-end">
                  <Button :label="t('ADD')" :disabled="isSchoolArchived" @click="showSchoolModuleModal()"/>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <DataTable
                      :value="modules"
                      striped-rows
                      paginator
                      size="small"
                      :rows="perPage"
                      :rowHover="true"
                      :alwaysShowPaginator="false"
                      :removableSort="true"
                  >
                    <template #empty>{{ t('NO_RESULTS') }}</template>
                    <Column field="code" :header="t('CODE_NAME')" sortable/>
                    <Column field="moduleName" :header="t('NAME')" sortable/>
                    <Column field="moduleType" :header="t('MODULE_TYPE')" sortable/>
                    <Column>
                      <template #body="{ data }">
                        <div class="text-center">
                          <Button
                              icon="pi pi-times"
                              :disabled="isSchoolArchived"
                              severity="danger"
                              v-tooltip="t('REMOVE')"
                              text
                              rounded
                              @click="removeModule(data.id)"
                          />
                        </div>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="3">
              <TrainerTab
                  tabTitle="SCHOOL_TRAINERS"
                  :trainers="trainers"
                  :isArchived="isSchoolArchived"
                  :trainerFields="trainerFields"
                  :allowedToEdit="isAdmin()"
                  @removeTrainer="removeTrainer"
                  @submitTrainer="submitTrainer"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>

    <school-module-modal
        :fields="moduleFields"
        :trainings="missingModules"
        @update-complete="handleUpdateComplete"
        ref="schoolModuleModalRef"
    />
  </div>
</template>

<script setup>
import schoolModuleModal from "@/views/components/modals/schoolModuleModal.vue";
import TrainerTab from "@/views/components/TrainerTab.vue";
import {computed, onMounted, ref, watch} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import EventBus from "@/util/eventBus";
import SearchInput from "@/components/SearchInput.vue";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import {useToast} from "@/util/toastUtils";
import {getGroupLangOptions} from "@/model/group";

const axios = injectAxios();
const auth = injectAuth();
const route = useRoute();
const {t, locale} = useI18n();
const toast = useToast();
const schoolData = ref(null);
const managers = ref([]);
const trainers = ref([]);
const modules = ref([]);
const missingModules = ref([]);
const isSearch = ref(false);
const groupsTableRef = ref(null);
const perPage = 15;
const {dateFormat} = useDateFormat();

onMounted(() => {
  if (props.school) {
    schoolData.value = props.school;
  }
  getSchool();
  getSchoolManagers();
  getSchoolTrainers();
  getSchoolModules();
});

const props = defineProps(["school"]);

const groupFields = computed(() => {
  return [
    {key: "title", label: t("GROUP_TITLE"), sortable: true},
    {key: "startEvent", label: t("START"), sortable: true, formatter: dateFormat},
    {key: "endEvent", label: t("END"), sortable: true, formatter: dateFormat},
    {key: "translation", label: t("MODULE"), sortable: true},
    {key: "language", label: t("LANGUAGE"), sortable: true},
    {key: "location", label: t("LOCATION"), sortable: true},
    {key: "groupSize", label: t("OCCUPANCY"), sortable: true, formatter: getGroupSize},
    {key: "status", label: t("STATUS"), sortable: true, formatter: (value) => t(value)},
    {key: "action", label: "", sortable: false},
  ];
});

const handleUpdateComplete = (success) => {
  if (success) {
    getSchoolModules();
  }
};

const isAdmin = () => {
  return auth.isAuthenticated && auth.hasRole("ADMIN");
};

const isValidRegistryCode = computed(() => {
  return !!schoolData.value?.registryCode?.match(/^\d{8}$/);
});

const moduleFields = computed(() => {
  return [
    {key: "code", label: t("CODE_NAME"), sortable: true},
    {key: "translations", label: t("NAME"), sortable: true, formatter: getModuleName},
    {key: "type", label: t("MODULE_TYPE"), sortable: true, formatter: getTypeName},
    {key: "action", label: "", sortable: false},
  ];
});

const isSchoolArchived = computed(() => {
  return schoolData.value?.archived;
});

const getTypeName = (value) => {
  return t(value);
};

const getSchool = async () => {
  const resp = await axios.get(`/school/schools/manager/${route.params.id}`);
  schoolData.value = resp.data;
};

async function getMissingModules() {
  const resp = await axios.get(`/school/${route.params.id}/missing`);
  missingModules.value = resp.data;
}

const showSchoolModuleModal = () => {
  getMissingModules();
  EventBus.$emit("show-school-module-modal");
};

const getSchoolManagers = async () => {
  const resp = await axios.get(`/school/${route.params.id}/managers`);
  managers.value = resp.data;
};

const getSchoolTrainers = async () => {
  const resp = await axios.get(`/school/${route.params.id}/trainers`);
  trainers.value = resp.data;
};

const getSchoolGroups = async (params) => {
  params["currentLocale"] = locale.value;
  params["schoolId"] = route.params.id;
  return await axios.get(`/training/groups/search`, {params: params});
};

async function getSchoolModules() {
  const resp = await axios.get(`/school/${route.params.id}/training`);
  modules.value = resp.data.map((item) => ({
    ...item,
    moduleName: getModuleName(item.translations),
    moduleType: getTypeName(item.type),
  }));

  await getMissingModules();
}

const toggleSchoolArchive = async () => {
  const response = await axios.post(`/school/${route.params.id}/archive`);
  schoolData.value = response.data;
};

const getGroupSize = (value, key, item) => {
  return (item.numberOfTrainees ? item.numberOfTrainees : 0) + " / " + item.groupSize;
};

const getModuleName = (value) => {
  return value?.[locale.value];
};

function toggleSearchForm() {
  isSearch.value = !isSearch.value;
}

const searchManager = async (searchValue) => {
  const params = {fields: "SSN,NAME", searchValue};
  const response = await axios.get(`/account/search/free-managers`, {params});
  return response.data.content
      .filter((t) => !t.isArchived)
      .map((t) => ({value: t.id, label: `${t.fullName} (${t.ssn})`}));
};

async function submitManager(manager) {
  const response = await axios.post(`/school/${route.params.id}/manager/${manager.value}`);
  toast.makeSuccessToast(t("SAVED"));
  managers.value.push(response.data);
  isSearch.value = false;
  await getSchoolManagers();
}

async function submitTrainer(trainer) {
  const response = await axios.post(`/school/${route.params.id}/trainer/${trainer.value}`);
  toast.makeSuccessToast(t("SAVED"));
  trainers.value.push(response.data);
  isSearch.value = false;
  await getSchoolTrainers();
}

async function removeManager(id) {
  await axios.delete(`/school/${route.params.id}/manager/${id}`);
  toast.makeSuccessToast(t("REMOVED"));
  await getSchoolManagers();
}

async function removeTrainer(id) {
  await axios.delete(`/school/${route.params.id}/trainer/${id}`);
  toast.makeSuccessToast(t("REMOVED"));
  await getSchoolTrainers();
}

async function removeModule(id) {
  await axios.delete(`/school/${route.params.id}/module/${id}`);
  toast.makeSuccessToast(t("REMOVED"));
  await getSchoolModules();
}

const updateSchool = async () => {
  await axios.put(`/school/${route.params.id}`, schoolData.value);
  await getSchool();
  toast.makeSuccessToast(t("SAVED"));
};

const trainerFields = computed(() => [
  {key: "fullName", label: t("NAME"), sortable: true},
  {key: "ssn", label: t("SSN"), sortable: true},
]);

function resetGroupsTable() {
  groupsTableRef.value?.reset();
}

watch([locale], resetGroupsTable);
</script>
